import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../core/client';
import { siteKeys, STALE_REFERENCE_MS } from '../../core/query-keys';
import { invalidateFleet } from '../servers/servers.hooks';
import { siteEndpoints } from './sites.endpoints';
import type { Site, SiteAdmin, SiteAdminListResponse, SiteFormInput, SiteListResponse } from './sites.types';

/**
 * List all sites — cloud mode only (feeds the site selector + topbar switcher). The endpoint is
 * cloud-tier; pass `enabled: false` to keep it dormant where the component also mounts in local mode.
 */
export function useSites(enabled = true) {
  return useQuery({
    queryKey: siteKeys.list,
    queryFn: () => api.get<SiteListResponse>(siteEndpoints.list),
    enabled,
    // Reference data: one cached copy is the single source of truth for the picker,
    // the topbar switcher and the shell. Freshness comes from the mutations
    // (`invalidateFleet` covers `siteKeys.all`), not from re-asking on every mount.
    staleTime: STALE_REFERENCE_MS,
  });
}

/** A single site by id — used to validate/refresh the persisted selection. */
export function useSite(id: string) {
  return useQuery({
    queryKey: siteKeys.one(id),
    queryFn: () => api.get<Site>(siteEndpoints.one(id)),
    enabled: Boolean(id),
    retry: false,
    staleTime: STALE_REFERENCE_MS,
  });
}

// ── Admin site management (R-150, cloud + admin only) ────────────────────────────

/** Admin site list WITH address/code (distinct from the picker's `useSites`). */
export function useManageSites(enabled = true) {
  return useQuery({
    queryKey: siteKeys.manage,
    queryFn: () => api.get<SiteAdminListResponse>(siteEndpoints.manage),
    enabled,
    staleTime: STALE_REFERENCE_MS,
  });
}

export function useCreateSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SiteFormInput) => api.post<SiteAdmin>(siteEndpoints.create, body),
    onSuccess: () => invalidateFleet(qc),
  });
}

export function useUpdateSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: SiteFormInput & { id: string }) =>
      api.put<SiteAdmin>(siteEndpoints.update(id), body),
    onSuccess: () => invalidateFleet(qc),
  });
}

export function useDeleteSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(siteEndpoints.update(id)),
    onSuccess: () => invalidateFleet(qc),
  });
}
